import { Injectable, OnDestroy } from "@angular/core";
import {
    BehaviorSubject,
    Subscription,
    of,
    combineLatest,
    Observable
} from "rxjs";
import { switchMap, catchError, map, tap } from "rxjs/operators";
import { PersonalReportsService } from "../personal-reports/personal-reports.service";
import { AuthService } from "../auth/auth.service";
import { AuthState } from "../../entities/AuthState";

export interface UnreadReportsState {
    count: number;
    hasUnread: boolean;
    unreadFuelReports: number;
    unreadTachographReports: number;
}

@Injectable({
    providedIn: "root"
})
export class UnreadReportsService implements OnDestroy {
    private unreadCountSubject = new BehaviorSubject<number>(0);
    private unreadFuelReportsSubject = new BehaviorSubject<number>(0);
    private unreadTachographReportsSubject = new BehaviorSubject<number>(0);

    public unreadReportsState$ = combineLatest([
        this.unreadCountSubject.asObservable(),
        this.unreadFuelReportsSubject.asObservable(),
        this.unreadTachographReportsSubject.asObservable()
    ]).pipe(
        map(([count, unreadFuelReports, unreadTachographReports]) => ({
            count,
            hasUnread: count > 0,
            unreadFuelReports,
            unreadTachographReports
        }))
    );

    private authSubscription: Subscription;
    private unreadCountSubscription: Subscription;

    constructor(
        private personalReportsService: PersonalReportsService,
        private authService: AuthService
    ) {
        this.initializeAuthSubscription();
    }

    private initializeAuthSubscription() {
        this.authSubscription = this.authService.authState.subscribe(
            (authState: AuthState) => {
                if (authState.isAuthenticated()) {
                    this.fetchUnreadCount();
                } else {
                    this.unreadCountSubject.next(0);
                    this.unreadFuelReportsSubject.next(0);
                    this.unreadTachographReportsSubject.next(0);
                }
            }
        );
    }

    private fetchUnreadCount() {
        if (this.unreadCountSubscription) {
            this.unreadCountSubscription.unsubscribe();
        }
        this.unreadCountSubscription = this.personalReportsService
            .getUnreadCount()
            .pipe(
                catchError(() => {
                    return of({
                        count: 0,
                        unreadFuelReports: 0,
                        unreadTachographReports: 0
                    });
                })
            )
            .subscribe((response) => {
                this.unreadCountSubject.next(response?.count || 0);
                this.unreadFuelReportsSubject.next(
                    response?.unreadFuelReports || 0
                );
                this.unreadTachographReportsSubject.next(
                    response?.unreadTachographReports || 0
                );
            });
    }

    private optimisticMarkAsRead(reportType?: string) {
        const previousCount = this.unreadCountSubject.getValue();
        const previousFuelReports = this.unreadFuelReportsSubject.getValue();
        const previousTachographReports =
            this.unreadTachographReportsSubject.getValue();

        if (reportType === "fuel" && previousFuelReports > 0) {
            this.unreadFuelReportsSubject.next(previousFuelReports - 1);
            this.unreadCountSubject.next(Math.max(0, previousCount - 1));
        } else if (
            reportType === "tachograph" &&
            previousTachographReports > 0
        ) {
            this.unreadTachographReportsSubject.next(
                previousTachographReports - 1
            );
            this.unreadCountSubject.next(Math.max(0, previousCount - 1));
        }

        return {
            count: previousCount,
            unreadFuelReports: previousFuelReports,
            unreadTachographReports: previousTachographReports
        };
    }

    private rollbackOptimisticMarkAsRead(
        count: number,
        fuelReports: number,
        tachographReports: number
    ) {
        this.unreadCountSubject.next(count);
        this.unreadFuelReportsSubject.next(fuelReports);
        this.unreadTachographReportsSubject.next(tachographReports);
    }

    markReportAsRead(
        reportId: number,
        reportType?: string
    ): Observable<{
        count: number;
        unreadFuelReports?: number;
        unreadTachographReports?: number;
    }> {
        const previousState = this.optimisticMarkAsRead(reportType);

        return this.personalReportsService.markReportAsRead(reportId).pipe(
            switchMap(() => {
                return this.personalReportsService.getUnreadCount().pipe(
                    tap((response) => {
                        this.unreadCountSubject.next(response?.count || 0);
                        this.unreadFuelReportsSubject.next(
                            response?.unreadFuelReports || 0
                        );
                        this.unreadTachographReportsSubject.next(
                            response?.unreadTachographReports || 0
                        );
                    }),
                    catchError((error) => {
                        console.error("Error fetching unread count:", error);
                        this.rollbackOptimisticMarkAsRead(
                            previousState.count,
                            previousState.unreadFuelReports,
                            previousState.unreadTachographReports
                        );
                        return of(previousState);
                    })
                );
            }),
            catchError((error) => {
                console.error("Error marking report as read:", error);
                this.rollbackOptimisticMarkAsRead(
                    previousState.count,
                    previousState.unreadFuelReports,
                    previousState.unreadTachographReports
                );
                return of(previousState);
            })
        );
    }

    getUnreadReportsState(): UnreadReportsState {
        const count = this.unreadCountSubject.getValue();
        const unreadFuelReports = this.unreadFuelReportsSubject.getValue();
        const unreadTachographReports =
            this.unreadTachographReportsSubject.getValue();
        return {
            count,
            hasUnread: count > 0,
            unreadFuelReports,
            unreadTachographReports
        };
    }

    ngOnDestroy() {
        if (this.authSubscription) {
            this.authSubscription.unsubscribe();
        }
        if (this.unreadCountSubscription) {
            this.unreadCountSubscription.unsubscribe();
        }
    }
}
